/**
 * Unallocated Results: lab and imaging results that arrived without a clean patient link
 * (`/clinic/unallocated-results`, Docs/specs/unallocated-results.md). Matching stays on the client:
 * the inbox page scores each result against the patient list, and the chart banner against the
 * one patient that is open.
 */
import api, { Horse } from '../utils/api';
import { UnallocatedItem, MatchSuggestion } from '../types/inbox';

/** PENDING, TRIAGED (assigned + rejected + not ours) or ALL. */
export type InboxListStatus = 'PENDING' | 'TRIAGED' | 'ALL';

// ─── Scoring ─────────────────────────────────────────────────────────────────
//
// Cheap deterministic scorer — exact-id wins, then exact-name, then partials.
// Owner adds a small boost. >=80 = "high confidence" (one-click assign in UI).

const norm = (s?: string | null) => (s ?? '').trim().toLowerCase();

function scoreOne(item: UnallocatedItem, horse: Horse): MatchSuggestion {
  let score = 0;
  const reasons: string[] = [];

  const itemPid = norm(item.patientIdOnReport);
  const itemName = norm(item.patientNameOnReport);
  const itemOwner = norm(item.ownerOnReport);

  const hPid = norm(horse.patientId);
  const hName = norm(horse.name);

  if (itemPid && hPid && itemPid === hPid) {
    score += 90;
    reasons.push('PatientId exact match');
  }

  if (itemName && hName) {
    if (itemName === hName) {
      score += 70;
      reasons.push('Name exact match');
    } else if (itemName.includes(hName) || hName.includes(itemName)) {
      score += 35;
      reasons.push('Name partial match');
    } else if (looseSimilar(itemName, hName)) {
      score += 25;
      reasons.push('Name similar (typo-tolerant)');
    }
  }

  if (itemOwner && Array.isArray(horse.owners)) {
    const ownerNames = (horse.owners as Array<{ name?: string }>)
      .map(o => norm(o?.name))
      .filter(Boolean);
    if (ownerNames.some(o => o === itemOwner)) {
      score += 20;
      reasons.push('Owner exact match');
    } else if (
      ownerNames.some(o => o.includes(itemOwner) || itemOwner.includes(o))
    ) {
      score += 10;
      reasons.push('Owner partial match');
    }
  }

  // Cap at 100 so multiple boosts don't blow past it.
  score = Math.min(score, 100);

  const ownerName =
    Array.isArray(horse.owners) && horse.owners.length > 0
      ? (horse.owners as Array<{ name?: string }>)[0]?.name
      : undefined;

  return {
    horseId: horse.id,
    horseName: horse.name,
    patientId: horse.patientId,
    ownerName,
    score,
    reasons,
  };
}

/**
 * Cheap typo-tolerance: bigram Jaccard ≥ 0.5. Catches "Eqine HIP" → "Equine Hip"
 * without pulling in a Levenshtein dependency. Tunable in one spot.
 */
function looseSimilar(a: string, b: string): boolean {
  if (a.length < 3 || b.length < 3) return false;
  const bigrams = (s: string) => {
    const out = new Set<string>();
    for (let i = 0; i < s.length - 1; i++) out.add(s.slice(i, i + 2));
    return out;
  };
  const A = bigrams(a);
  const B = bigrams(b);
  let inter = 0;
  A.forEach(g => {
    if (B.has(g)) inter++;
  });
  const union = A.size + B.size - inter;
  return union > 0 && inter / union >= 0.5;
}

// ─── Public API ──────────────────────────────────────────────────────────────

export const inboxService = {
  /** Triage items for the caller's clinic, newest-arrived first. */
  async list(status: InboxListStatus = 'ALL'): Promise<UnallocatedItem[]> {
    const res = await api.get('/clinic/unallocated-results', { params: { status } });
    return res.data;
  },

  /** PENDING → ASSIGNED; the server links the underlying lab result or study to the horse. */
  async assign(id: string, horseId: string): Promise<UnallocatedItem> {
    const res = await api.post(`/clinic/unallocated-results/${encodeURIComponent(id)}/assign`, { horseId });
    return res.data;
  },

  /** Soft-archive: kept for audit, hidden from the Pending view. */
  async reject(id: string, status: 'REJECTED' | 'NOT_OURS', reason?: string): Promise<UnallocatedItem> {
    const res = await api.post(`/clinic/unallocated-results/${encodeURIComponent(id)}/reject`, { status, reason });
    return res.data;
  },

  /** Back to PENDING; an assigned result drops off the patient's chart while it is re-triaged. */
  async reopen(id: string): Promise<UnallocatedItem> {
    const res = await api.post(`/clinic/unallocated-results/${encodeURIComponent(id)}/reopen`);
    return res.data;
  },

  /** Top five patients for one result, best first. */
  scoreMatches(item: UnallocatedItem, horses: Horse[]): MatchSuggestion[] {
    return horses
      .map(h => scoreOne(item, h))
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  },
};
